import { css } from 'aphrodite';
import { Button } from '../../../../../web/components-v2/Button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../../web/components/HoverCard';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { getEntryKey, ISettingEntry } from './settingEntry';
import { SettingCategory } from './Setting';
import { ISettingsGroup, READ_ONLY_CATEGORY } from './settingsGroups';
import { settingsHubStyles as styles } from './settingsHubStyles';
import { filterSettings } from './settingsSearch';

/** How many rows each card previews before deferring to its link. */
const PREVIEW_COUNT = 3;

interface IProps {
	entries: ISettingEntry[];
	/** Groups to show. The caller omits any the current user may not see. */
	groups: ISettingsGroup[];
	onOpenGroup: (category: SettingCategory) => void;
}

/**
 * The settings landing view: one card per group, so 150-odd settings can be scanned rather than
 * read. Cards summarise; every value is still changed inside the group.
 */
export function SettingsOverviewHub({ entries, groups, onOpenGroup }: IProps) {
	return (
		// `Permanent` takes a cell in the same grid rather than a full-width row of its own. It is
		// the eighth of eight, so it pairs off with the seventh and the hub costs four rows instead
		// of five — and a card that summarises four values has never needed the width of two.
		<div className={css(styles.groupGrid)}>
			{groups.map(group => {
				const Card = group.category === READ_ONLY_CATEGORY ? ReadOnlyGroupCard : SettingsGroupCard;
				return <Card key={group.category} entries={entries} group={group} onOpen={() => onOpenGroup(group.category)} />;
			})}
		</div>
	);
}

/**
 * The way into a group.
 *
 * The header already carries the group's size, so this counts what the card is holding back rather
 * than saying the same number twice. When it is holding nothing back — every row already on the
 * card — there is no remainder to name and the link is only the way in.
 *
 * The visible text opens the accessible name instead of being replaced by it: eight cards carry
 * this control, so the name has to say which group, and voice control still has to match what is
 * on screen.
 */
function ViewGroupLink({
	category,
	onOpen,
	remaining,
}: {
	category: SettingCategory;
	onOpen: () => void;
	remaining: number;
}) {
	const label = remaining > 0 ? `View ${remaining} more` : 'View all';

	return (
		<Button
			variant='link'
			size='sm'
			onClick={onOpen}
			styles={[styles.inlineLinkButton, styles.cardLink]}
			aria-label={`${label} ${category} settings`}
		>
			{label} &rarr;
		</Button>
	);
}

/**
 * A group's description, behind an icon rather than a line of its own — with eight cards, eight
 * descriptions cost more height than they earn.
 *
 * HoverCard is what the settings rows already use for their own help text, so the gesture is the
 * one this tab has always had. Hover-card content is not reliably announced, and the description is
 * the only place a group explains itself, so the trigger carries it as its accessible name too.
 */
function GroupDescriptionTooltip({ category, description }: { category: string; description: string }) {
	return (
		<HoverCard openDelay={100}>
			<HoverCardTrigger className={css(styles.cardInfoIcon)} aria-label={`${category}: ${description}`}>
				<InfoIcon fillColor='currentColor' />
			</HoverCardTrigger>
			<HoverCardContent styles={[styles.cardTooltip]}>{description}</HoverCardContent>
		</HoverCard>
	);
}

function SettingsGroupCard({
	entries,
	group,
	onOpen,
}: {
	entries: ISettingEntry[];
	group: ISettingsGroup;
	onOpen: () => void;
}) {
	const groupEntries = filterSettings(entries, { category: group.category });
	// Sub-rows only make sense beneath their parent, so previews show top-level rows.
	const previewable = groupEntries.filter(entry => !entry.isSubRow && entry.labelText);
	const preview = previewable.slice(0, PREVIEW_COUNT);
	const { Icon } = group;

	return (
		// A container rather than one large button: a button wrapping a heading and a value list
		// flattens both into a single accessible name. The explicit link below is the control.
		<div className={css(styles.card)}>
			<div className={css(styles.cardHeader)}>
				<Icon className={css(styles.cardIcon)} />
				<span className={css(styles.cardTitle)}>{group.category}</span>
				<GroupDescriptionTooltip category={group.category} description={group.description} />
				<span className={css(styles.cardCount)}>
					{groupEntries.length} {groupEntries.length === 1 ? 'setting' : 'settings'}
				</span>
			</div>
			<div className={css(styles.previewList)}>
				{preview.map((entry, index) => (
					<div key={getEntryKey(entry, index)} className={css(styles.previewRow)}>
						<span className={css(styles.previewLabel)}>{entry.labelText}</span>
						<span className={css(styles.previewValue, entry.valueText === 'On' && styles.previewValueOn)}>
							{entry.valueText ?? '—'}
						</span>
					</div>
				))}
			</div>
			<ViewGroupLink category={group.category} onOpen={onOpen} remaining={groupEntries.length - preview.length} />
		</div>
	);
}

/**
 * `Permanent` is fixed at account creation, so its card lists values instead of inviting edits.
 *
 * It still opens. The grid can only summarise a row that has a value, and not every Permanent row
 * does — `Reset Account` is an action, so it summarises as a dash. Without a way in, the group page
 * holding those controls was reachable only by opening some other group first and crossing on its
 * tabs.
 */
function ReadOnlyGroupCard({
	entries,
	group,
	onOpen,
}: {
	entries: ISettingEntry[];
	group: ISettingsGroup;
	onOpen: () => void;
}) {
	const groupEntries = filterSettings(entries, { category: group.category });
	// The grid can only list a row it can put a name to. The count below stays the group's real
	// size regardless, so the card never disagrees with the group page it opens onto.
	const listable = groupEntries.filter(entry => entry.labelText);
	const { Icon } = group;

	return (
		<div className={css(styles.card)}>
			<div className={css(styles.cardHeader)}>
				<Icon className={css(styles.cardIcon)} />
				<span className={css(styles.cardTitle)}>{group.category}</span>
				<GroupDescriptionTooltip category={group.category} description={group.description} />
				<span className={css(styles.readOnlyBadge)}>READ ONLY</span>
				<span className={css(styles.cardCount)}>
					{groupEntries.length} {groupEntries.length === 1 ? 'setting' : 'settings'}
				</span>
			</div>
			<div className={css(styles.readOnlyGrid)}>
				{listable.map((entry, index) => (
					<div key={getEntryKey(entry, index)} className={css(styles.readOnlyItem)}>
						<span className={css(styles.readOnlyLabel)}>{entry.labelText}</span>
						<span className={css(styles.readOnlyValue)}>{entry.valueText ?? '—'}</span>
					</div>
				))}
			</div>
			<ViewGroupLink category={group.category} onOpen={onOpen} remaining={groupEntries.length - listable.length} />
		</div>
	);
}
